/**
 * The ladder's spot it and dictation cards (v1 ticket 11). Spot it is the
 * first look's reading version: tap the word in its line, then pick its
 * meaning. Dictation is step 3's listening version: the lesson plays the word
 * (its audioText) and the learner types it, graded like a GapFill.
 */
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DictationApi, SpotItApi } from '../api';
import { ExerciseElement } from '../base';
import { currentTarget, t } from '../../i18n';
import { grade, gradeOptionsFor, spikesFor, type Miss } from '../../grade.generated';
import { answerInput, firstSound, markedAnswer, revealedAnswer, shownAnswer } from './exercises';
import { iso, tPart, ui, uiDir } from '../../bidi-html';
import { signHostOf, signZone } from '../sign';
import { giveUpButton, giveUpOffered } from '../give-up';
import { explainRepeats } from '../explain-repeats';

/** Target-language text isolated in its own direction, so a line reads right inside a Persian interface. */
export function isolate(text: string) {
  const input = answerInput(text);
  return { dir: input.dir === 'rtl' ? 'rtl' : 'ltr', lang: input.lang ?? nothing };
}

/** Whether a spot it line can be played aloud: a Finglish line (a Persian reader's) has no voice to read it right. */
export function hearableLine(line: string): boolean {
  return !(answerInput(line).lang ?? '').endsWith('-Latn');
}

// ---- SpotIt -----------------------------------------------------------------

@customElement('li-spotit')
export class LiSpotIt extends ExerciseElement<typeof SpotItApi> {
  protected readonly api = SpotItApi;
  /** The word tapped in the line, and the meaning picked once the word was found. */
  @state() private tapped: number | null = null;
  @state() private picked: number | null = null;
  protected resetState() { this.tapped = null; this.picked = null; }

  render() {
    const p = this.controller?.props;
    if (!p) return nothing;
    const found = this.tapped !== null && p.at.includes(this.tapped);
    const missed = this.tapped !== null && !found;
    const giveUp = this.picked === null && !missed && giveUpOffered(this) ? giveUpButton(this.giveUp) : nothing;
    const word = isolate(p.word);
    const line = isolate(p.words.join(' '));
    const token = (i: number) => {
      if (this.tapped === null) return '';
      if (p.at.includes(i)) return 'correct';
      return i === this.tapped ? 'wrong' : '';
    };
    const after = html`
      ${p.explain ? html`<div class="note" dir=${uiDir(p.explain)}>${iso(p.explain)}</div>` : nothing}
      ${p.translation ? html`<div class="note" dir=${uiDir(p.translation)}>${iso(p.translation)}</div>` : nothing}`;
    const sign = signHostOf(this);
    if (sign) {
      // Hanging as a sign (the dream sign): the line is the bubble, one beat at a time sits in the thumb zone.
      const wordEl = html`<bdi class="spot-word" dir=${word.dir} lang=${word.lang}>${p.word}</bdi>`;
      const { top, foot } = sign.signParts();
      const right = this.picked === p.correct; // -1: gave up
      const meaning = html`<bdi dir="auto">${p.options[p.correct]}</bdi>`;
      const beat = this.tapped === null
        ? html`<div class="sign-prompt sign-fade" dir=${ui()}>${tPart('sign.find', 'word', wordEl)}</div>${giveUp}`
        : missed
          ? html`<div class="sign-prompt" dir=${ui()}>${tPart('sign.find', 'word', wordEl)}</div>
              <div class="result bad spot-missed sign-fade" dir=${ui()}>${t(hearableLine(p.words.join(' ')) ? 'sign.notThat' : 'ex.spotitMissed')}</div>`
          : html`<div class="spot-found">${this.picked === null
            ? html`<div class="sign-prompt sign-fade" dir=${ui()}>${tPart('sign.means', 'word', wordEl)}</div>
                <div class="options">
                  ${p.options.map((o, i) => html`<button class="option" @click=${() => this.pick(i)} dir="auto">${o}</button>`)}
                </div>${giveUp}`
            : right
              ? html`<div class="sign-prompt sign-fade" dir=${ui()}>${wordEl} <span class="sign-meaning">— ${meaning}</span></div>`
              : html`<div class="sign-prompt" dir=${ui()}>${wordEl}</div>
                  <div class="result bad sign-fade" dir=${ui()}>${tPart('ex.answerWas', 'a', meaning)}</div>`}</div>`;
      const done = missed || this.picked !== null;
      const repeated = this.picked !== null && typeof p.explain === 'string' && explainRepeats(p.explain, p.word, String(p.options[p.correct]));
      const note = done && p.explain && !repeated ? html`<div class="note" dir=${uiDir(p.explain)}>${iso(p.explain)}</div>` : nothing;
      return html`
        <div class="ex spotit sign">
          <bdi class="spot-line" dir=${line.dir} lang=${line.lang}>
            ${p.words.map((w, i) => html`<button class="word spot-token ${token(i)}" ?disabled=${this.tapped !== null} @click=${() => this.tap(i)}>${w}</button>`)}
          </bdi>
          ${done && p.translation
            ? html`<div class="sign-translation sign-fade" dir=${uiDir(p.translation)}>${iso(p.translation)}</div>`
            : nothing}
          <div class="sign-zone" dir=${ui()}>${top}<div class="sign-beat">${beat}${note}</div>${foot}</div>
        </div>`;
    }
    return html`
      <div class="ex spotit">
        <div class="label">${t('ex.spotit')}</div>
        <bdi class="spot-word" dir=${word.dir} lang=${word.lang}>${p.word}</bdi>
        <bdi class="spot-line" dir=${line.dir} lang=${line.lang}>
          ${p.words.map((w, i) => html`<button class="word spot-token ${token(i)}" ?disabled=${this.tapped !== null} @click=${() => this.tap(i)}>${w}</button>`)}
        </bdi>
        ${this.tapped === null ? giveUp : nothing}
        ${missed ? html`<div class="result bad spot-missed" dir=${ui()}>${t('ex.spotitMissed')}${after}</div>` : nothing}
        ${found
          ? html`
              <div class="result ok spot-found" dir=${ui()}>${t('ex.spotitFound')}</div>
              <div class="big-ca" style="font-size:20px;margin-top:8px" dir=${uiDir(p.question)}>${iso(p.question)}</div>
              <div class="options">
                ${p.options.map((o, i) => html`
                  <button
                    class="option ${this.picked !== null && i === p.correct ? 'correct' : ''} ${i === this.picked && i !== p.correct ? 'wrong' : ''}"
                    ?disabled=${this.picked !== null}
                    @click=${() => this.pick(i)}
                    dir="auto"
                  >${o}</button>`)}
              </div>
              ${giveUp}
              ${this.picked !== null
                ? html`<div class="result ${this.picked === p.correct ? 'ok' : 'bad'}">
                    <span dir=${ui()}>${this.picked === p.correct ? t('ex.correct') : tPart('ex.answerWas', 'a', html`<bdi dir="auto">${p.options[p.correct]}</bdi>`)}</span>
                    ${after}
                  </div>`
                : nothing}`
          : nothing}
      </div>
    `;
  }

  /** A wrong tap is the card's scored miss (the right word is then marked); a right one asks the meaning. */
  private tap(i: number) {
    const p = this.controller?.props;
    if (!p || this.tapped !== null) return;
    this.tapped = i;
    if (!p.at.includes(i)) this.send('answer', { value: p.words[i], correct: false, expected: p.word });
  }

  /**
   * "I don't know — show me" (ticket 46), on either beat: the word is marked in the line and its meaning shown
   * (no meaning picked: -1), and an ordinary wrong answer is sent.
   */
  private giveUp = () => {
    const p = this.controller?.props;
    if (!p || this.picked !== null || (this.tapped !== null && !p.at.includes(this.tapped))) return;
    this.tapped = p.at[0] ?? 0;
    this.picked = -1;
    this.send('answer', { value: '', correct: false, gaveUp: true, expected: p.options[p.correct] });
  };

  private pick(i: number) {
    const p = this.controller?.props;
    if (!p || this.picked !== null || this.tapped === null || !p.at.includes(this.tapped)) return;
    this.picked = i;
    this.send('answer', { value: p.options[i], correct: i === p.correct, expected: p.options[p.correct] });
  }
}
export const SpotIt = { ...SpotItApi, tagName: 'li-spotit' };

// ---- Dictation --------------------------------------------------------------

@customElement('li-dictation')
export class LiDictation extends ExerciseElement<typeof DictationApi> {
  protected readonly api = DictationApi;
  @state() private value = '';
  /** As GapFill's: `scratch` passed with a spike missed (ticket 05), `shown` after Show me. */
  @state() private result: 'ok' | 'scratch' | 'bad' | 'shown' | null = null;
  @state() private scratch: { expected: string; missed: Miss[] } | null = null;
  @state() private hints: string[] = [];
  @state() private disputed = false;
  protected resetState() { this.value = ''; this.result = null; this.scratch = null; this.hints = []; this.disputed = false; }

  /** Rescue tiers, as GapFill's: first sound, then the card's hint. Hints never score. */
  private tiers(): string[] {
    const p = this.controller?.props;
    if (!p) return [];
    const out = [t('ex.startsWith', { s: firstSound(p.answer.split(/\s+/)[0] ?? p.answer) })];
    if (p.hint) out.push(p.hint);
    return out;
  }

  render() {
    const p = this.controller?.props;
    if (!p) return nothing;
    const left = this.tiers().length - this.hints.length;
    const input = answerInput(p.answer);
    const sign = signHostOf(this);
    const meaning = p.translation ? html`<div class="note" dir=${uiDir(p.translation)}>${iso(p.translation)}</div>` : nothing;
    const hints = this.hints.length ? html`<div class="note" dir=${ui()}>💡 ${iso(this.hints.join(t('common.dot')))}</div>` : nothing;
    // Hanging as a sign, Show me waits in the thumb zone's foot once a miss has brought it there.
    const giveUp = this.result === null && giveUpOffered(this);
    const ownShowMe = !giveUp && !(sign && this.result === 'bad');
    // Hanging, the meaning joins the bubble once answered; the verdict stays in the thumb zone.
    const withMeaning = sign ? nothing : meaning;
    const tail = this.result === 'scratch' && this.scratch
      ? html`<div class="result ok scratch" dir=${ui()}>${t('ex.scratch')} ${markedAnswer(this.scratch.expected, this.scratch.missed)}</div>${withMeaning}`
      : this.result === 'ok'
      ? html`<div class="result ok" dir=${ui()}>${t('ex.correct')} — ${shownAnswer(p.answer)}</div>${withMeaning}`
      : this.result === 'shown'
      ? html`
          <input class="gap-input dictation-input" type="text" dir=${input.dir} lang=${input.lang ?? nothing}
            aria-label=${t('ex.typeHeard')} placeholder=${t('ex.typeHeard')} .value=${this.value} readonly />
          ${revealedAnswer(this.value, p.answer)}${withMeaning}`
      : html`
          <input
            class="gap-input dictation-input"
            type="text"
            autocapitalize="off"
            autocomplete="off"
            dir=${input.dir}
            lang=${input.lang ?? nothing}
            placeholder=${t('ex.typeHeard')}
            .value=${this.value}
            @input=${(e: Event) => (this.value = (e.target as HTMLInputElement).value)}
            @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.check()}
          />
          <div class="actions">
            <button class="btn primary" @click=${this.check} ?disabled=${!this.value.trim()}>${t('ex.check')}</button>
            ${left > 0 ? html`<button class="btn" @click=${this.takeHint}>${left > 1 ? t('ex.hintN', { n: left }) : t('ex.hint')}</button>` : nothing}
            ${ownShowMe ? html`<button class="btn ghost" @click=${this.showAnswer}>${t('ex.showMe')}</button>` : nothing}
          </div>
          ${giveUp ? giveUpButton(this.giveUp) : nothing}
          ${this.result === 'bad'
            ? html`<div class="result bad">${t('ex.notQuiteHint')}</div>
                <div class="actions">
                  ${this.disputed
                    ? html`<span class="muted small">${t('ex.flagged')}</span>`
                    : html`<button class="btn ghost" @click=${this.dispute}>${t('ex.shouldCount')}</button>`}
                </div>`
            : nothing}
        `;
    if (sign) {
      // The word is only heard (the tag's play button): the bubble stays quiet until it is answered, then holds
      // the meaning; the question, the box and its buttons sit in the thumb zone.
      const done = this.result !== null && this.result !== 'bad';
      return html`
        <div class="ex dictation sign">
          ${done && p.translation ? html`<div class="sign-translation sign-fade" dir=${uiDir(p.translation)}>${iso(p.translation)}</div>` : nothing}
          ${signZone(sign, html`${done ? nothing : html`<div class="sign-prompt sign-fade" dir=${ui()}>${t('sign.heard')}</div>`}${hints}${tail}`)}
        </div>`;
    }
    return html`
      <div class="ex dictation">
        <div class="label">${t('ex.dictation')}</div>
        ${hints}${tail}
      </div>
    `;
  }

  private takeHint = () => {
    const tiers = this.tiers();
    if (this.hints.length >= tiers.length) return;
    this.hints = [...this.hints, tiers[this.hints.length]];
    this.send('hint', { tier: this.hints.length });
  };

  /** The spelling grader (ticket 05), as the server runs it: a missed spike is a scratch, and a scratch passes. */
  private check = () => {
    const p = this.controller?.props;
    if (!p || !this.value.trim()) return;
    const v = grade(this.value, [p.answer, ...(p.accept ?? []).map((a) => String(a))], spikesFor(currentTarget()), gradeOptionsFor(currentTarget()));
    this.scratch = v.kind === 'scratched' ? { expected: v.expected, missed: v.missed } : null;
    this.result = v.kind === 'right' ? 'ok' : v.kind === 'scratched' ? 'scratch' : 'bad';
    this.send('answer', {
      value: this.value, correct: v.kind !== 'wrong', expected: p.answer, hintsUsed: this.hints.length,
      ...(v.kind === 'scratched' ? { scratched: true, missed: v.missed } : {}),
    });
  };

  private dispute = () => {
    const p = this.controller?.props;
    if (!p || this.disputed) return;
    this.disputed = true;
    this.send('dispute', { value: this.value, expected: p.answer, hintsUsed: this.hints.length });
  };

  /** "I don't know — show me" (ticket 46): the answer shows and an ordinary wrong answer is sent, with no text. */
  private giveUp = () => {
    const p = this.controller?.props;
    if (!p || this.result !== null) return;
    this.result = 'shown';
    this.scratch = null;
    this.send('answer', { value: '', correct: false, gaveUp: true, expected: p.answer, hintsUsed: this.hints.length });
  };

  /** Show the answer: the card's own Show me, and the lesson bar's after a miss (ticket 32). */
  showAnswer = () => {
    const p = this.controller?.props;
    if (!p) return;
    this.result = 'shown';
    this.scratch = null;
    this.send('answer', { value: p.answer, correct: false, revealed: true, expected: p.answer, hintsUsed: this.hints.length });
  };
}
export const Dictation = { ...DictationApi, tagName: 'li-dictation' };
